/**
 * Translations for the mini-games (earn-coins) side track.
 * Covers GamesPage (index) + MathBlitzPage.
 *
 * Convention: `games.<screen>.<key>`. Reward / cap numbers are interpolated
 * via {amount} / {cap} so the same string works for any future game.
 */
const games = {
  zh: {
    // ─── Index page (/games) ────────────────────────────────────────────
    'games.index.title': '🎮 小游戏赚金币',
    'games.index.subtitle': '每个游戏有每日上限 — 玩好就停～',
    'games.index.progressChip': '今日 +{amount} / {cap} 金币',
    'games.index.cappedChip': '今天已领满 ✨',
    'games.index.playBadge': '今日已玩 {used}/{cap} 次',
    'games.index.dailyPlayCapHit': '今天这个小游戏已玩满 {cap} 次',
    'games.index.startBtn': '开始',
    'games.index.cappedBtn': '明天再来',
    'games.index.dailyPlayCapBtn': '明天再玩',
    'games.index.ageFit': '推荐 {age} 岁',
    'games.index.ageOther': '{age} 岁内容',
    'games.index.parentDisabled': '家长暂时关闭了这个小游戏',
    'games.index.parentDisabledBtn': '已关闭',
    'games.index.noneEnabled': '家长还没有开启可玩的小游戏，先去陪宠物或赚金币吧～',

    'games.colorMatch.cardTitle': '🎨 颜色小侦探',
    'games.colorMatch.cardSubtitle': '看颜色，选对答案',
    'games.colorMatch.cardReward': '+4 金币一题，推荐 3-5 岁',
    'games.shapePairs.cardTitle': '🔷 形状配对',
    'games.shapePairs.cardSubtitle': '圆形、三角形、星星都来找朋友',
    'games.shapePairs.cardReward': '+4 金币一题，推荐 3-6 岁',
    'games.petBubblePop.cardTitle': '🫧 宠物泡泡拍拍',
    'games.petBubblePop.cardSubtitle': '帮宠物点破正确泡泡',
    'games.petBubblePop.cardReward': '+3 金币一题，推荐 3 岁',
    'games.petRainbowSnacks.cardTitle': '🌈 彩虹零食盘',
    'games.petRainbowSnacks.cardSubtitle': '按颜色把零食放进宠物碗',
    'games.petRainbowSnacks.cardReward': '+3 金币一题，推荐 4 岁',
    'games.petShapeNest.cardTitle': '🏠 小窝形状家',
    'games.petShapeNest.cardSubtitle': '给宠物小窝配对形状',
    'games.petShapeNest.cardReward': '+4 金币一题，推荐 5 岁',
    'games.petPicnicWords.cardTitle': '🧺 宠物野餐单词',
    'games.petPicnicWords.cardSubtitle': '在野餐篮里认识简单英文',
    'games.petPicnicWords.cardReward': '+4 金币一题，推荐 6 岁',
    'games.petCoinCatch.cardTitle': '🪙 宠物接金币',
    'games.petCoinCatch.cardSubtitle': '边接金币边练简单加减',
    'games.petCoinCatch.cardReward': '+5 金币一题，推荐 7 岁',
    'games.petTreatPath.cardTitle': '🍪 零食路线',
    'games.petTreatPath.cardSubtitle': '帮宠物找到安全的零食路',
    'games.petTreatPath.cardReward': '+5 金币一题，推荐 8 岁',
    'games.wordMatch.cardTitle': '🔤 单词配对',
    'games.wordMatch.cardSubtitle': '把中文和英文单词连起来',
    'games.wordMatch.cardReward': '+5 金币一题，推荐 6-8 岁',
    'games.patternPath.cardTitle': '🧩 规律小径',
    'games.patternPath.cardSubtitle': '找出下一步的规律',
    'games.patternPath.cardReward': '+7 金币一题，推荐 9-11 岁',
    'games.petPatternTrain.cardTitle': '🚂 宠物列车规律',
    'games.petPatternTrain.cardSubtitle': '看宠物列车下一节是什么',
    'games.petPatternTrain.cardReward': '+6 金币一题，推荐 9 岁',
    'games.petRescueMap.cardTitle': '🗺️ 宠物救援地图',
    'games.petRescueMap.cardSubtitle': '规划路线，把宠物安全带回来',
    'games.petRescueMap.cardReward': '+7 金币一题，推荐 10 岁',
    'games.petMysteryClues.cardTitle': '🔎 宠物线索侦探',
    'games.petMysteryClues.cardSubtitle': '根据线索推理宠物去了哪里',
    'games.petMysteryClues.cardReward': '+7 金币一题，推荐 11 岁',
    'games.budgetBuilder.cardTitle': '🧾 预算规划师',
    'games.budgetBuilder.cardSubtitle': '用预算做聪明选择',
    'games.budgetBuilder.cardReward': '+8 金币一题，推荐 12-14 岁',
    'games.petBudgetCafe.cardTitle': '☕ 宠物小店预算',
    'games.petBudgetCafe.cardSubtitle': '帮宠物小店算预算和找零',
    'games.petBudgetCafe.cardReward': '+8 金币一题，推荐 12 岁',
    'games.petCareStrategy.cardTitle': '🧭 宠物照护策略',
    'games.petCareStrategy.cardSubtitle': '根据状态决定先照顾什么',
    'games.petCareStrategy.cardReward': '+9 金币一题，推荐 13 岁',
    'games.petCityPlanner.cardTitle': '🏙️ 宠物城市规划',
    'games.petCityPlanner.cardSubtitle': '为宠物城市安排基础设施',
    'games.petCityPlanner.cardReward': '+10 金币一题，推荐 14 岁',

    // Math-blitz card on index
    'games.mathBlitz.cardTitle': '⚡ 数学闪电',
    'games.mathBlitz.cardSubtitle': '30 秒速算挑战',
    'games.mathBlitz.cardReward': '+5 金币一题，上限 50 金币 / 天',

    // ─── Math-blitz page (/games/math-blitz) ────────────────────────────
    'games.mathBlitz.title': '⚡ 数学闪电',
    'games.mathBlitz.rules.title': '玩法',
    'games.mathBlitz.rules.line1': '30 秒内做对越多越好。',
    'games.mathBlitz.rules.line2': '答对一题 +5 金币，每天最多领 50 金币。',
    'games.mathBlitz.rules.line3': '答错不扣分，下一题继续！',
    'games.mathBlitz.startBtn': '开始',
    'games.mathBlitz.cappedNotice': '今天 +50 金币已经领满啦，明天再来玩～',
    'games.mathBlitz.timeLeft': '剩余 {sec} 秒',
    'games.mathBlitz.score': '答对 {n}',
    'games.mathBlitz.done.title': '时间到！',
    'games.mathBlitz.done.score': '答对 {n} 题',
    'games.mathBlitz.done.reward': '获得 +{amount} 金币',
    'games.mathBlitz.done.cappedToday': '今天奖励上限已经领满啦',
    'games.mathBlitz.done.zeroReward': '加油，下次答对就有奖励啦！',
    'games.mathBlitz.done.againBtn': '再玩一次',
    'games.mathBlitz.done.tomorrowBtn': '明天再来',
    'games.mathBlitz.done.backBtn': '回到小游戏',

    // v0.10.2 — per-hour cap UI on /games index
    'games.index.hourBadge':  '本小时 {used}/{cap}',
    'games.index.hourCapHit': '⏳ 待 {at} 后再来玩',
    'games.index.hourCapBtn': '稍后再来',

    'games.generic.score': '答对 {n}',
    'games.generic.round': '第 {n}/{total} 题',
    'games.generic.correct': '答对啦',
    'games.generic.wrong': '差一点',
    'games.generic.doneTitle': '完成挑战',
    'games.generic.reward': '获得 +{amount} 金币',
    'games.generic.noReward': '今天奖励已到上限',
    'games.generic.playLimitReached': '今天这个小游戏已玩满，明天再玩',
    'games.generic.playAgain': '再玩一次',
    'games.generic.back': '回到小游戏',

    // ─── Memory Digits (/games/memory-digits) ─────────────────────────────
    'games.memDigits.cardTitle': '🧠 数字记忆',
    'games.memDigits.cardSubtitle': '看一眼记下来 — 越来越长的数字',
    'games.memDigits.cardReward': '记一位 +8 金币，每天最多 80 金币，每小时 3 次',
    'games.potionPuzzle.cardTitle': '🧪 魔法药水配方',
    'games.potionPuzzle.cardSubtitle': '交换药水，连成 3 个以上帮宠物完成配方',
    'games.potionPuzzle.cardReward': '每完成 1 个配方 +4 金币，每天最多 60 金币',
    'games.potionPuzzle.title': '🧪 魔法药水配方',
    'games.potionPuzzle.subtitle': '宠物炼金小实验',
    'games.potionPuzzle.helper': '点一个药水，再点旁边的药水交换。横向或纵向连成 3 个以上就会装进锅里。',
    'games.potionPuzzle.prompt': '试着把同样的药水连在一起。',
    'games.potionPuzzle.moves': '还剩 {n} 步',
    'games.potionPuzzle.recipeBadge': '配方 {n}/{goal}',
    'games.potionPuzzle.matchMessage': '装进锅里 {n} 个药水 ✨',
    'games.potionPuzzle.noMatch': '这一步没有配方，再换一个相邻药水试试。',
    'games.potionPuzzle.shuffle': '重新摆放',
    'games.potionPuzzle.finish': '完成',
    'games.potionPuzzle.doneRecipes': '完成 {n} 个配方',
    'games.memDigits.title': '数字记忆',
    'games.memDigits.subtitle': '智力小挑战 · 看几秒就要记住',
    'games.memDigits.howTitle': '怎么玩',
    'games.memDigits.how1': '屏幕会闪一组数字（1-2 秒）',
    'games.memDigits.how2': '消失后把它打回来',
    'games.memDigits.how3': '答对就升级 — 数字会一次比一次长',
    'games.memDigits.startBtn': '开始挑战',
    'games.memDigits.roundLabel': '回合 {n} 位',
    'games.memDigits.scoreLabel': '已记 {n}',
    'games.memDigits.typeBack': '快把刚才的数字打回来！',
    'games.memDigits.autoSubmit': '位数填满会自动提交',
    'games.memDigits.wrongTitle': '差一点～',
    'games.memDigits.wrongHint': '正确答案：{target}',
    'games.memDigits.doneScore': '🎉 共记住 {n} 位',
    'games.memDigits.dailyToday': '今日 {earned} / {cap} 金币',
    'games.memDigits.hourLeft': '本小时还可玩 {n} 次',
    'games.memDigits.hourCapHit': '⏳ {at} 后再来玩',
    'games.memDigits.playAgain': '再玩一次',
    'games.memDigits.backToGames': '回到小游戏',
    'games.memDigits.backHome': '回家陪宠物',
    'games.memDigits.dailyCapReached': '今日金币已领满',
    'games.memDigits.dailyCapDetail': '每天最多 80 金币，明天再来挑战～',
    'games.memDigits.dailyPlayCapReached': '今日次数已玩满',
    'games.memDigits.dailyPlayCapDetail': '这个小游戏今天已经玩了 {cap} 次，明天再来挑战～',
    'games.memDigits.hourCapReached': '本小时已玩满',
    'games.memDigits.hourCapDetail': '每小时最多 {cap} 次，{at} 后可以继续',
    'games.memDigits.hourBadge': '本小时已玩 {used}/{cap}',

    'games.error.generic': '出了点小状况，等等再试',
  },
  en: {
    // ─── Index page (/games) ────────────────────────────────────────────
    'games.index.title': '🎮 Mini-games — earn coins',
    'games.index.subtitle': 'Each game has a daily cap — play smart, then stop~',
    'games.index.progressChip': 'Today +{amount} / {cap} coins',
    'games.index.cappedChip': "Today's cap reached ✨",
    'games.index.playBadge': 'Played {used}/{cap} today',
    'games.index.dailyPlayCapHit': 'This mini-game has reached {cap} plays today',
    'games.index.startBtn': 'Play',
    'games.index.cappedBtn': 'Come back tomorrow',
    'games.index.dailyPlayCapBtn': 'Play tomorrow',
    'games.index.ageFit': 'Ages {age}',
    'games.index.ageOther': 'Ages {age} content',
    'games.index.parentDisabled': 'A parent turned this mini-game off for now',
    'games.index.parentDisabledBtn': 'Disabled',
    'games.index.noneEnabled': 'No playable mini-games are enabled yet. Spend time with your pet or earn coins first~',

    'games.colorMatch.cardTitle': '🎨 Color Detective',
    'games.colorMatch.cardSubtitle': 'Look at the color, pick the match',
    'games.colorMatch.cardReward': '+4 coins per correct, best for ages 3-5',
    'games.shapePairs.cardTitle': '🔷 Shape Pairs',
    'games.shapePairs.cardSubtitle': 'Match circles, triangles, stars, and more',
    'games.shapePairs.cardReward': '+4 coins per correct, best for ages 3-6',
    'games.petBubblePop.cardTitle': '🫧 Pet Bubble Pop',
    'games.petBubblePop.cardSubtitle': 'Pop the right bubble for your pet',
    'games.petBubblePop.cardReward': '+3 coins per correct, best for age 3',
    'games.petRainbowSnacks.cardTitle': '🌈 Rainbow Treat Tray',
    'games.petRainbowSnacks.cardSubtitle': 'Sort colorful treats into the pet bowl',
    'games.petRainbowSnacks.cardReward': '+3 coins per correct, best for age 4',
    'games.petShapeNest.cardTitle': '🏠 Shape Nest',
    'games.petShapeNest.cardSubtitle': 'Match shapes for a cozy pet nest',
    'games.petShapeNest.cardReward': '+4 coins per correct, best for age 5',
    'games.petPicnicWords.cardTitle': '🧺 Pet Picnic Words',
    'games.petPicnicWords.cardSubtitle': 'Learn simple English words at a pet picnic',
    'games.petPicnicWords.cardReward': '+4 coins per correct, best for age 6',
    'games.petCoinCatch.cardTitle': '🪙 Pet Coin Catch',
    'games.petCoinCatch.cardSubtitle': 'Practice easy addition while catching coins',
    'games.petCoinCatch.cardReward': '+5 coins per correct, best for age 7',
    'games.petTreatPath.cardTitle': '🍪 Treat Path',
    'games.petTreatPath.cardSubtitle': 'Guide your pet along the safe snack route',
    'games.petTreatPath.cardReward': '+5 coins per correct, best for age 8',
    'games.wordMatch.cardTitle': '🔤 Word Match',
    'games.wordMatch.cardSubtitle': 'Connect Chinese and English words',
    'games.wordMatch.cardReward': '+5 coins per correct, best for ages 6-8',
    'games.patternPath.cardTitle': '🧩 Pattern Path',
    'games.patternPath.cardSubtitle': 'Find what comes next',
    'games.patternPath.cardReward': '+7 coins per correct, best for ages 9-11',
    'games.petPatternTrain.cardTitle': '🚂 Pet Pattern Train',
    'games.petPatternTrain.cardSubtitle': 'Find the next car in the pet train',
    'games.petPatternTrain.cardReward': '+6 coins per correct, best for age 9',
    'games.petRescueMap.cardTitle': '🗺️ Pet Rescue Map',
    'games.petRescueMap.cardSubtitle': 'Plan the route and bring your pet home',
    'games.petRescueMap.cardReward': '+7 coins per correct, best for age 10',
    'games.petMysteryClues.cardTitle': '🔎 Pet Clue Detective',
    'games.petMysteryClues.cardSubtitle': 'Use clues to infer where the pet went',
    'games.petMysteryClues.cardReward': '+7 coins per correct, best for age 11',
    'games.budgetBuilder.cardTitle': '🧾 Budget Builder',
    'games.budgetBuilder.cardSubtitle': 'Make smart choices with a budget',
    'games.budgetBuilder.cardReward': '+8 coins per correct, best for ages 12-14',
    'games.petBudgetCafe.cardTitle': '☕ Pet Cafe Budget',
    'games.petBudgetCafe.cardSubtitle': 'Help the pet cafe count budgets and change',
    'games.petBudgetCafe.cardReward': '+8 coins per correct, best for age 12',
    'games.petCareStrategy.cardTitle': '🧭 Pet Care Strategy',
    'games.petCareStrategy.cardSubtitle': 'Choose what to care for first',
    'games.petCareStrategy.cardReward': '+9 coins per correct, best for age 13',
    'games.petCityPlanner.cardTitle': '🏙️ Pet City Planner',
    'games.petCityPlanner.cardSubtitle': 'Plan useful spaces for a pet city',
    'games.petCityPlanner.cardReward': '+10 coins per correct, best for age 14',

    // Math-blitz card on index
    'games.mathBlitz.cardTitle': '⚡ Math Blitz',
    'games.mathBlitz.cardSubtitle': '30-second arithmetic sprint',
    'games.mathBlitz.cardReward': '+5 coins per correct, up to 50 coins / day',

    // ─── Math-blitz page (/games/math-blitz) ────────────────────────────
    'games.mathBlitz.title': '⚡ Math Blitz',
    'games.mathBlitz.rules.title': 'How it works',
    'games.mathBlitz.rules.line1': 'Solve as many as you can in 30 seconds.',
    'games.mathBlitz.rules.line2': '+5 coins per correct, up to 50 coins / day.',
    'games.mathBlitz.rules.line3': 'No penalty for wrong — just keep going!',
    'games.mathBlitz.startBtn': 'Start',
    'games.mathBlitz.cappedNotice': "Today's +50 coin cap is full — come back tomorrow~",
    'games.mathBlitz.timeLeft': '{sec}s left',
    'games.mathBlitz.score': '{n} correct',
    'games.mathBlitz.done.title': "Time's up!",
    'games.mathBlitz.done.score': '{n} correct',
    'games.mathBlitz.done.reward': 'You earned +{amount} coins',
    'games.mathBlitz.done.cappedToday': "Today's reward cap is full",
    'games.mathBlitz.done.zeroReward': 'Nice try — get one right next time for the reward!',
    'games.mathBlitz.done.againBtn': 'Play again',
    'games.mathBlitz.done.tomorrowBtn': 'Come back tomorrow',
    'games.mathBlitz.done.backBtn': 'Back to mini-games',

    // v0.10.2 — per-hour cap UI on /games index
    'games.index.hourBadge':  'This hour {used}/{cap}',
    'games.index.hourCapHit': '⏳ Come back after {at}',
    'games.index.hourCapBtn': 'Wait a bit',

    'games.generic.score': '{n} correct',
    'games.generic.round': 'Question {n}/{total}',
    'games.generic.correct': 'Correct',
    'games.generic.wrong': 'Almost',
    'games.generic.doneTitle': 'Challenge complete',
    'games.generic.reward': 'You earned +{amount} coins',
    'games.generic.noReward': "Today's reward cap is full",
    'games.generic.playLimitReached': "Today's play count is full — come back tomorrow",
    'games.generic.playAgain': 'Play again',
    'games.generic.back': 'Back to mini-games',

    // ─── Memory Digits (/games/memory-digits) ─────────────────────────────
    'games.memDigits.cardTitle': '🧠 Digit Memory',
    'games.memDigits.cardSubtitle': 'See it once, then type it back',
    'games.memDigits.cardReward': '+8 coins per digit, up to 80 coins / day, 3× per hour',
    'games.potionPuzzle.cardTitle': '🧪 Puzzling Potions Lab',
    'games.potionPuzzle.cardSubtitle': 'Swap potions and match 3+ to finish pet recipes',
    'games.potionPuzzle.cardReward': '+4 coins per recipe, up to 60 coins / day',
    'games.potionPuzzle.title': '🧪 Puzzling Potions Lab',
    'games.potionPuzzle.subtitle': 'Pet alchemy puzzle',
    'games.potionPuzzle.helper': 'Tap one potion, then tap a neighbor to swap. Match 3 or more in a row or column to brew.',
    'games.potionPuzzle.prompt': 'Try matching the same potions together.',
    'games.potionPuzzle.moves': '{n} moves left',
    'games.potionPuzzle.recipeBadge': 'Recipes {n}/{goal}',
    'games.potionPuzzle.matchMessage': 'Brewed {n} potion drops ✨',
    'games.potionPuzzle.noMatch': 'No recipe from that swap. Try a different neighbor.',
    'games.potionPuzzle.shuffle': 'Shuffle',
    'games.potionPuzzle.finish': 'Finish',
    'games.potionPuzzle.doneRecipes': 'Finished {n} recipes',
    'games.memDigits.title': 'Digit Memory',
    'games.memDigits.subtitle': 'Brain workout · catch the number before it vanishes',
    'games.memDigits.howTitle': 'How to play',
    'games.memDigits.how1': 'A number flashes for 1-2 seconds',
    'games.memDigits.how2': 'Type it back after it disappears',
    'games.memDigits.how3': 'Correct → next round adds one more digit',
    'games.memDigits.startBtn': 'Start',
    'games.memDigits.roundLabel': 'Round: {n} digits',
    'games.memDigits.scoreLabel': 'Recalled {n}',
    'games.memDigits.typeBack': 'Type the digits you just saw!',
    'games.memDigits.autoSubmit': 'Auto-submits when length matches',
    'games.memDigits.wrongTitle': 'So close~',
    'games.memDigits.wrongHint': 'It was: {target}',
    'games.memDigits.doneScore': '🎉 You remembered {n} digits',
    'games.memDigits.dailyToday': 'Today {earned} / {cap} coins',
    'games.memDigits.hourLeft': '{n} plays left this hour',
    'games.memDigits.hourCapHit': '⏳ Come back after {at}',
    'games.memDigits.playAgain': 'Play again',
    'games.memDigits.backToGames': 'Back to mini-games',
    'games.memDigits.backHome': 'Home with your pet',
    'games.memDigits.dailyCapReached': "Today's coin cap reached",
    'games.memDigits.dailyCapDetail': 'Max 80 coins/day — try again tomorrow!',
    'games.memDigits.dailyPlayCapReached': "Today's play count is full",
    'games.memDigits.dailyPlayCapDetail': 'This mini-game has already been played {cap} times today. Try again tomorrow!',
    'games.memDigits.hourCapReached': 'Hourly limit reached',
    'games.memDigits.hourCapDetail': 'Max {cap} plays per hour. Resumes after {at}',
    'games.memDigits.hourBadge': 'This hour: {used}/{cap}',

    'games.error.generic': 'Something hiccuped, try again soon',
  },
};

export default games;
